import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { downloadDataUrl } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import InstallAppButton from "@/components/InstallAppButton";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import PushToggleButton from "@/components/PushToggleButton";
import { CoursesSummary } from "@/components/CoursesSummary";
import { ClosuresSection } from "@/components/calendar/ClosuresSection";
import { CourseMetaSection } from "@/components/calendar/CourseMetaSection";
import { CourseCalendar } from "@/components/calendar/CourseCalendar";
import { SchoolCalendar } from "@/components/calendar/SchoolCalendar";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Unlock, Lock, Download, Users, CalendarDays, FileText, Save, RotateCcw, Bell, UserX, GraduationCap, CreditCard, BadgeEuro, Pencil, FileDown, Upload, Eraser, Archive, ArchiveRestore, Search, Ticket, CalendarClock, RefreshCw, BadgeCheck, StickyNote, Circle, ChevronDown, Settings2, GripVertical, Eye, EyeOff } from "lucide-react";

const LOGO = "https://www.areteteatro.com/wp-content/uploads/2020/03/logo-arete.png";

const ST = {
  locked: { l: "Bloccata", c: "bg-muted text-muted-foreground" },
  unlocked: { l: "Sbloccata", c: "bg-primary/15 text-primary" },
  form_signed: { l: "Modulo firmato", c: "bg-blue-500/15 text-blue-600" },
  partially_paid: { l: "1ª rata pagata", c: "bg-amber-500/15 text-amber-600" },
  enrolled: { l: "Saldato ✓", c: "bg-green-500/15 text-green-600" },
};
const FILTERS = [{ k: "all", l: "Tutti" }, { k: "direct", l: "Registrati" }, { k: "from_trial", l: "In prova" }, { k: "enrolled", l: "Iscritti" }, { k: "unpaid", l: "Da pagare" }, { k: "partial", l: "Prima rata" }, { k: "paid", l: "Saldato" }, { k: "archived", l: "Archiviati" }];
const METHODS = [{ k: "carta", l: "Carta/Bancomat" }, { k: "contanti", l: "Contanti" }, { k: "bonifico", l: "Bonifico" }, { k: "satispay", l: "Satispay" }];
const METHOD_L = { carta: "Carta/Bancomat", contanti: "Contanti", bonifico: "Bonifico", satispay: "Satispay", stripe: "Online (carta)" };
const ARCI_METHODS = [{ k: "contanti", l: "Contanti" }, { k: "bancomat", l: "Bancomat" }, { k: "satispay", l: "Satispay" }, { k: "bonifico", l: "Bonifico" }];
const ARCI_METHOD_L = { contanti: "Contanti", bancomat: "Bancomat", satispay: "Satispay", bonifico: "Bonifico" };
const GROUPS = [{ k: "all", l: "Tutti" }, { k: "from_trial", l: "In prova" }, { k: "direct", l: "Registrati" }, { k: "enrolled", l: "Iscritti" }, { k: "archived", l: "Archiviati" }];
const PAY_FILTERS = [{ k: "all", l: "Tutti" }, { k: "unpaid", l: "Da pagare" }, { k: "partial", l: "Prima rata" }, { k: "paid", l: "Saldato" }];
const ARCI_FILTERS = [{ k: "all", l: "Tutti" }, { k: "paid", l: "Pagata" }, { k: "unpaid", l: "Non pagata" }];
const GROUP_BADGE = { from_trial: { l: "In prova", c: "bg-teal-500/15 text-teal-700" }, direct: { l: "Registrato", c: "bg-slate-400/20 text-slate-600" }, enrolled: { l: "Iscritto", c: "bg-blue-500/15 text-blue-600" }, archived: { l: "Archiviato", c: "bg-zinc-500/15 text-zinc-600" } };
const PAY_BADGE = { unpaid: { l: "Da pagare", c: "bg-rose-500/15 text-rose-600" }, partial: { l: "Prima rata", c: "bg-amber-500/15 text-amber-600" }, paid: { l: "Saldato", c: "bg-green-500/15 text-green-600" } };

export default function Admin() {
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "users");
  const [adminYear, setAdminYear] = useState(null);
  const [calYear, setCalYear] = useState(null);
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [slot, setSlot] = useState({ date: "", time: "", location: "", capacity: 10 });
  const [cfg, setCfg] = useState(null);
  const [enrollCode, setEnrollCode] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);
  const [filter, setFilter] = useState("all");
  const [courseFilter, setCourseFilter] = useState("all");
  const [payFilter, setPayFilter] = useState("all");
  const [arciFilter, setArciFilter] = useState("all");
  const [openCard, setOpenCard] = useState(null);
  const [courseView, setCourseView] = useState(null);
  const [calVisible, setCalVisible] = useState({}); // corso -> calendario visibile agli allievi
  const [coursePay, setCoursePay] = useState("all");
  const [courseArci, setCourseArci] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [addQ, setAddQ] = useState("");
  const [search, setSearch] = useState("");
  const [emailEdits, setEmailEdits] = useState({});
  const [nameEdits, setNameEdits] = useState({});
  const [phoneEdits, setPhoneEdits] = useState({});
  const [noteEdits, setNoteEdits] = useState({});
  const [arciPay, setArciPay] = useState(null);
  const [arciMethod, setArciMethod] = useState("");
  const [arciRevert, setArciRevert] = useState(null);
  const [notif, setNotif] = useState(null);
  const [savingNotif, setSavingNotif] = useState(false);
  const [payMethod, setPayMethod] = useState("carta");
  const [editSlot, setEditSlot] = useState(null);
  const [moveBk, setMoveBk] = useState(null);
  const [moveTarget, setMoveTarget] = useState("");
  const importRef = useRef(null);
  const metaRef = useRef(null);
  // Anno & Corsi tab
  const [yearCfg, setYearCfg] = useState(null);
  const [yearCfgYear, setYearCfgYear] = useState("");
  const [coursesText, setCoursesText] = useState("");
  const [courseRows, setCourseRows] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [savingYear, setSavingYear] = useState(false);

  const loadUsers = async (y) => {
    const { data: d } = await api.get("/admin/users", { params: y ? { year: y } : {} });
    setData(d); setAdminYear(d.year);
    if (!yearCfgYear) setYearCfgYear(d.current_year);
  };
  // Optimistic helpers: aggiornano subito la UI locale, con rollback su errore
  const patchUserLocal = (uid, patchFn) =>
    setData((prev) => (prev ? { ...prev, users: prev.users.map((u) => (u.user_id === uid ? patchFn(u) : u)) } : prev));
  const restoreUsers = (users) => setData((prev) => (prev ? { ...prev, users } : prev));
  // Ricarica dal server SOLO l'iscritto coinvolto e aggiorna solo quel record
  const loadUser = async (userId) => {
    try {
      const { data: u } = await api.get(`/admin/users/${userId}`, { params: { year: adminYear } });
      patchUserLocal(userId, () => u);
    } catch { /* la UI resta sull'ultimo stato ottimistico */ }
  };
  const runOptimistic = async (uid, patchFn, call, okMsg) => {
    const prevUsers = data.users;
    patchFn();
    try {
      await call();
      if (okMsg) toast.success(okMsg);
      loadUser(uid);
      return true;
    } catch (err) { restoreUsers(prevUsers); toast.error(apiError(err.response?.data?.detail)); return false; }
  };
  const loadRest = async () => {
    const [s, b, c, n] = await Promise.all([api.get("/slots"), api.get("/bookings"), api.get("/enrollment-config"), api.get("/admin/notification-config")]);
    setSlots(s.data); setBookings(b.data);
    setCfg({ title: c.data.title || "", body: c.data.body || "", privacy_text: c.data.privacy_text || "", fields: c.data.fields || [] });
    setNotif(n.data);
  };
  useEffect(() => { loadUsers(); loadRest(); }, []);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("q");
    if (q) { setSearch(q); setFilter("all"); }
  }, []);

  useEffect(() => {
    if (tab !== "courses" || !adminYear) return;
    api.get("/admin/course-meta", { params: { year: adminYear } })
      .then((r) => setCalVisible(Object.fromEntries(r.data.map((m) => [m.course, m])))).catch(() => {});
  }, [tab, adminYear]);
  useEffect(() => { setOpenCard(null); }, [search, filter, payFilter, arciFilter, courseFilter, adminYear, courseView, coursePay, courseArci]);

  useEffect(() => {
    api.get("/admin/enrollment-code", { params: { year: adminYear } })
      .then(({ data }) => setEnrollCode(data.code || ""))
      .catch(() => {});
  }, [adminYear]);

  const saveEnrollCode = async () => {
    try {
      await api.post("/admin/enrollment-code", { code: enrollCode.trim(), year: adminYear });
      toast.success("Codice di iscrizione salvato");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") { loadUsers(adminYear); loadRest(); } };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
  }, [adminYear]);

  const loadYearCfg = async (y) => {
    const { data: d } = await api.get("/admin/year-config", { params: { year: y } });
    setYearCfg(d); setCoursesText((d.courses || []).join("\n"));
    setCourseRows((d.courses || []).map((c) => ({ orig: c, name: c })));
  };
  useEffect(() => { if (yearCfgYear) loadYearCfg(yearCfgYear); }, [yearCfgYear]);

  const toggle = async (uid, unlock) => {
    await runOptimistic(uid,
      () => patchUserLocal(uid, (u) => ({ ...u, enrollment: { ...(u.enrollment || {}), unlocked: unlock, status: unlock ? "unlocked" : "locked" } })),
      () => api.post(`/admin/enrollment/${uid}/${unlock ? "unlock" : "lock"}`, null, { params: { year: adminYear } }),
      unlock ? "Iscrizione sbloccata" : "Iscrizione bloccata");
  };
  const dlPdf = async (uid) => {
    try {
      const { data: d } = await api.get(`/admin/enrollment/${uid}/pdf`, { params: { year: adminYear } });
      if (!d.signed_pdf) return toast.error("Modulo non disponibile");
      downloadDataUrl(d.signed_pdf, `modulo-${uid}.pdf`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const delUser = async (uid) => {
    const prevUsers = data.users;
    setData((prev) => (prev ? { ...prev, users: prev.users.filter((u) => u.user_id !== uid) } : prev));
    try { await api.delete(`/admin/users/${uid}`); toast.success("Allievo eliminato"); }
    catch (err) { restoreUsers(prevUsers); toast.error(apiError(err.response?.data?.detail)); }
  };
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const moveCourseRow = (from, to) => {
    if (from === null || to === null || from === to) return;
    setCourseRows((rows) => {
      const next = rows.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };
  const savePhone = async (uid, newPhone) => {
    const ph = (newPhone || "").trim();
    const prevUsers = data.users;
    patchUserLocal(uid, (u) => ({ ...u, phone: ph }));
    try {
      await api.put(`/admin/users/${uid}/phone`, { phone: ph });
      toast.success("Telefono aggiornato");
      setPhoneEdits((s) => { const c = { ...s }; delete c[uid]; return c; });
      loadUser(uid);
    } catch (err) { restoreUsers(prevUsers); toast.error(apiError(err.response?.data?.detail)); }
  };
  const saveName = async (uid, newName) => {
    const nm = (newName || "").trim();
    if (!nm) return toast.error("Il nome non può essere vuoto");
    const prevUsers = data.users;
    patchUserLocal(uid, (u) => ({ ...u, name: nm }));
    try {
      await api.put(`/admin/users/${uid}/name`, { name: nm });
      toast.success("Nome aggiornato");
      setNameEdits((s) => { const c = { ...s }; delete c[uid]; return c; });
      loadUser(uid);
    } catch (err) { restoreUsers(prevUsers); toast.error(apiError(err.response?.data?.detail)); }
  };
  const saveEmail = async (uid, newEmail) => {
    const em = (newEmail || "").trim().toLowerCase();
    if (!EMAIL_RE.test(em)) return toast.error("Inserisci un indirizzo email valido");
    const prevUsers = data.users;
    patchUserLocal(uid, (u) => ({ ...u, email: em }));
    try {
      await api.put(`/admin/users/${uid}/email`, { email: em });
      toast.success("Email aggiornata");
      setEmailEdits((s) => { const c = { ...s }; delete c[uid]; return c; });
      loadUser(uid);
    } catch (err) { restoreUsers(prevUsers); toast.error(apiError(err.response?.data?.detail)); }
  };
  const rejectForm = async (uid) => {
    await runOptimistic(uid,
      () => patchUserLocal(uid, (u) => ({ ...u, enrollment: { ...(u.enrollment || {}), status: "unlocked", unlocked: true, signed_at: null } })),
      () => api.post(`/admin/enrollment/${uid}/reject-form`, null, { params: { year: adminYear } }),
      "Modulo rifiutato: l'allievo è stato avvisato di rifirmare");
  };
  const exportData = async () => {
    try {
      const { data: d } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(d, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `areteteatro-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Backup esportato");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const exportCsv = () => {
    const ST_LABEL = { locked: "Bloccata", unlocked: "Sbloccata", form_signed: "Modulo firmato", partially_paid: "Prima rata pagata", enrolled: "Saldato" };
    const list = data.users.filter((u) => !u.archived);
    const header = ["Nome", "Email", "Telefono", "Provenienza", "Stato iscrizione", "Corso", "Pagato (€)", "Residuo (€)"];
    const rows = list.map((u) => {
      const e = u.enrollment || {};
      return [u.name || "", u.email || "", u.phone || "", u.from_trial ? "In prova" : "Utente",
        ST_LABEL[e.status] || "Bloccata", e.course || "", (paidOf(u) || 0).toFixed(2), (e.amount_due || 0).toFixed(2)];
    });
    const nTrial = list.filter((u) => u.from_trial).length;
    rows.push([]);
    rows.push([`Totale: ${list.length}`, `In prova: ${nTrial}`, `Utenti: ${list.length - nTrial}`]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `iscritti-${adminYear.replace("/", "-")}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("Elenco iscritti esportato (CSV)");
  };
  const importData = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const payload = JSON.parse(await file.text());
      const { data: d } = await api.post("/admin/import", payload);
      const tot = Object.values(d.imported || {}).reduce((a, b) => a + b, 0);
      toast.success(`Backup importato: ${tot} record ripristinati`);
      loadUsers(adminYear); loadRest();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "File di backup non valido"); }
  };
  const setUserCourse = async (uid, course) => {
    const c = course === "__none__" ? "" : course;
    await runOptimistic(uid,
      () => patchUserLocal(uid, (u) => ({ ...u, enrollment: { ...(u.enrollment || {}), course: c } })),
      () => api.post(`/admin/enrollment/${uid}/course`, { course: c, year: adminYear }),
      "Corso aggiornato");
  };
  const moveCourse = async (uid, course) => {
    await runOptimistic(uid,
      () => patchUserLocal(uid, (u) => ({ ...u, enrollment: { ...(u.enrollment || {}), course } })),
      () => api.post(`/admin/enrollment/${uid}/course`, { course, year: adminYear }),
      "Iscritto spostato di corso");
  };
  const removeFromCourse = async (uid) => {
    await runOptimistic(uid,
      () => patchUserLocal(uid, (u) => ({ ...u, enrollment: { ...(u.enrollment || {}), course: "" } })),
      () => api.post(`/admin/enrollment/${uid}/course`, { course: "", year: adminYear }),
      "Iscritto rimosso dal corso");
  };
  const addToCourse = async (uid, course) => {
    await runOptimistic(uid,
      () => patchUserLocal(uid, (u) => ({ ...u, enrollment: { ...(u.enrollment || {}), course } })),
      () => api.post(`/admin/enrollment/${uid}/course`, { course, year: adminYear }),
      "Iscritto aggiunto al corso");
  };
  const saveMasterNote = async (uid, note) => {
    const prevUsers = data.users;
    patchUserLocal(uid, (u) => ({ ...u, enrollment: { ...(u.enrollment || {}), master_note: note } }));
    try {
      await api.post(`/admin/enrollment/${uid}/master-fields`, { master_note: note, year: adminYear });
      toast.success("Nota salvata");
      setNoteEdits((s) => { const c = { ...s }; delete c[uid]; return c; });
      loadUser(uid);
    } catch (err) { restoreUsers(prevUsers); toast.error(apiError(err.response?.data?.detail)); }
  };
  const toggleArci = async (uid, paid) => {
    await runOptimistic(uid,
      () => patchUserLocal(uid, (u) => ({ ...u, enrollment: { ...(u.enrollment || {}), arci_paid: paid, arci_method: paid ? (u.enrollment?.arci_method || "") : "" } })),
      () => api.post(`/admin/enrollment/${uid}/master-fields`, { arci_paid: paid, year: adminYear }),
      paid ? "Tessera Arci segnata come pagata" : "Tessera Arci annullata");
  };
  const confirmArciPaid = async () => {
    if (!arciMethod) return toast.error("Seleziona il metodo di pagamento");
    const uid = arciPay?.uid;
    const method = arciMethod;
    setArciPay(null); setArciMethod("");
    await runOptimistic(uid,
      () => patchUserLocal(uid, (u) => ({ ...u, enrollment: { ...(u.enrollment || {}), arci_paid: true, arci_method: method } })),
      () => api.post(`/admin/enrollment/${uid}/master-fields`, { arci_paid: true, arci_method: method, year: adminYear }),
      "Tessera ARCI segnata come pagata");
  };
  const confirmArciRevert = async () => {
    const uid = arciRevert?.uid;
    setArciRevert(null);
    await runOptimistic(uid,
      () => patchUserLocal(uid, (u) => ({ ...u, enrollment: { ...(u.enrollment || {}), arci_paid: false, arci_method: "" } })),
      () => api.post(`/admin/enrollment/${uid}/master-fields`, { arci_paid: false, arci_method: "", year: adminYear }),
      "Tessera ARCI segnata come NON pagata");
  };
  void toggleArci;
  const manualPay = async (uid, plan) => {
    const prevUsers = data.users;
    try {
      const { data: enr } = await api.post(`/admin/enrollment/${uid}/manual-payment`, { plan, method: payMethod, year: adminYear });
      patchUserLocal(uid, (u) => ({ ...u, enrollment: { ...(u.enrollment || {}), ...enr } }));
      toast.success("Pagamento registrato");
      loadUser(uid);
    } catch (err) { restoreUsers(prevUsers); toast.error(apiError(err.response?.data?.detail)); }
  };
  const undoPay = async (uid) => {
    const prevUsers = data.users;
    try {
      const { data: enr } = await api.post(`/admin/enrollment/${uid}/undo-payment`, null, { params: { year: adminYear } });
      patchUserLocal(uid, (u) => ({ ...u, enrollment: { ...(u.enrollment || {}), ...enr } }));
      toast.success("Ultimo pagamento annullato");
      loadUser(uid);
    } catch (err) { restoreUsers(prevUsers); toast.error(apiError(err.response?.data?.detail)); }
  };
  const setArchived = async (uid, archived) => {
    await runOptimistic(uid,
      () => patchUserLocal(uid, (u) => ({ ...u, archived })),
      () => api.post(`/admin/users/${uid}/archive`, { archived }),
      archived ? "Utente archiviato" : "Utente ripristinato");
  };

  const addSlot = async (e) => { e.preventDefault(); try { await api.post("/slots", { ...slot, capacity: Number(slot.capacity) }); toast.success("Data aggiunta"); setSlot({ date: "", time: "", location: "", capacity: 10 }); loadRest(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } };
  const delSlot = async (id) => { try { await api.delete(`/slots/${id}`); loadRest(); } catch {} };
  const cancelBooking = async (bid) => {
    try { await api.delete(`/admin/bookings/${bid}`); toast.success("Prenotazione cancellata"); loadRest(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const doMoveBooking = async () => {
    if (!moveTarget) return toast.error("Seleziona una nuova data");
    try { await api.post(`/admin/bookings/${moveBk.id}/move`, { slot_id: moveTarget }); toast.success("Prenotazione spostata (l'allievo è stato avvisato via email)"); setMoveBk(null); setMoveTarget(""); loadRest(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const saveEditSlot = async () => {
    try { await api.patch(`/slots/${editSlot.id}`, { date: editSlot.date, time: editSlot.time, location: editSlot.location, capacity: Number(editSlot.capacity) }); toast.success("Data aggiornata"); setEditSlot(null); loadRest(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const toggleSlotClosed = async (s) => {
    try { await api.patch(`/slots/${s.id}`, { closed: !s.closed }); toast.success(!s.closed ? "Prenotazioni chiuse" : "Prenotazioni riaperte"); loadRest(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const downloadAttendees = async (s) => {
    try {
      const { data: d } = await api.get(`/admin/slots/${s.id}/attendees`);
      if (!d.count) return toast.info("Nessun iscritto per questa data");
      const statusLabel = { confirmed: "Confermata", cancelled: "Annullata", pending: "In attesa" };
      const dateIt = s.date ? s.date.slice(0, 10).split("-").reverse().join("/") : "";
      const header = ["Nome", "Cognome", "Email", "Telefono", "Data lezione", "Ora lezione", "Luogo", "Stato prenotazione", "Note"];
      const rows = [header, ...d.attendees.map((a) => {
        const parts = (a.name || "").trim().split(/\s+/).filter(Boolean);
        const nome = parts.shift() || "";
        const cognome = parts.join(" ");
        return [nome, cognome, a.email || "", a.phone || "", dateIt, s.time || "", s.location || "", statusLabel[a.status] || a.status || "", a.note || ""];
      })];
      const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\r\n");
      const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = `iscritti-prova-${s.date}-${(s.time || "").replace(/[:.]/g, "")}.csv`; a.click();
      toast.success(`${d.count} iscritti esportati`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const bookingsFor = (slotId) => bookings.filter((b) => b.slot_id === slotId);

  const setField = (i, patch) => setCfg({ ...cfg, fields: cfg.fields.map((f, idx) => idx === i ? { ...f, ...patch } : f) });
  const addField = () => setCfg({ ...cfg, fields: [...cfg.fields, { label: "", required: true }] });
  const removeField = (i) => setCfg({ ...cfg, fields: cfg.fields.filter((_, idx) => idx !== i) });
  const saveCfg = async () => {
    if (!cfg.title.trim()) return toast.error("Inserisci un titolo per il modulo");
    if (cfg.fields.some((f) => !f.label.trim())) return toast.error("Ogni campo deve avere un'etichetta");
    setSavingCfg(true);
    try { await api.post("/admin/enrollment-config", cfg); toast.success("Modulo salvato"); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSavingCfg(false); }
  };
  const resetCfg = async () => { try { const { data: d } = await api.delete("/admin/enrollment-config"); setCfg({ title: d.title || "", body: d.body || "", privacy_text: d.privacy_text || "", fields: d.fields || [] }); toast.success("Modulo ripristinato"); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } };

  const setReminder = (i, patch) => setNotif({ ...notif, reminders: notif.reminders.map((r, idx) => idx === i ? { ...r, ...patch } : r) });
  const addReminder = () => setNotif({ ...notif, reminders: [...(notif.reminders || []), { value: 1, unit: "giorni" }] });
  const removeReminder = (i) => setNotif({ ...notif, reminders: notif.reminders.filter((_, idx) => idx !== i) });
  const saveNotif = async () => { setSavingNotif(true); try { await api.post("/admin/notification-config", notif); toast.success("Notifiche salvate"); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSavingNotif(false); } };

  const saveYear = async () => {
    if (!yearCfgYear.trim()) return toast.error("Inserisci l'anno (es. 2026/2027)");
    setSavingYear(true);
    const courses = courseRows.map((r) => r.name.trim()).filter(Boolean);
    const renames = courseRows
      .filter((r) => r.orig && r.name.trim() && r.name.trim() !== r.orig)
      .map((r) => ({ from: r.orig, to: r.name.trim() }));
    try {
      // Docenti e saggi modificati e non ancora salvati (prima del rinomino dei corsi)
      if (yearCfg.year === yearCfgYear) await metaRef.current?.saveDirty();
      await api.post("/admin/year-config", { full_amount: Number(yearCfg.full_amount), installment_amount: Number(yearCfg.installment_amount), courses, renames, installment_start: yearCfg.installment_start || "", installment_deadline: yearCfg.installment_deadline || "" }, { params: { year: yearCfgYear } });
      toast.success(renames.length ? "Anno e corsi salvati · iscritti aggiornati" : "Anno e corsi salvati");
      loadYearCfg(yearCfgYear);
      loadUsers(adminYear);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSavingYear(false); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const paidOf = (u) => (u.payments || []).reduce((a, p) => a + (p.amount || 0), 0);
  const bucketOf = (u) => { const s = u.enrollment?.status; if (s === "enrolled") return "paid"; if (s === "partially_paid") return "partial"; if (u.enrollment?.unlocked && paidOf(u) === 0) return "unpaid"; return "other"; };
  const hasCourse = (u) => !!(u.enrollment?.course);
  const submitted = (u) => { const s = u.enrollment?.status; return !!u.enrollment?.signed_at || ["form_signed", "partially_paid", "enrolled"].includes(s); };
  const groupOf = (u) => u.archived ? "archived" : (submitted(u) ? "enrolled" : (u.from_trial ? "from_trial" : "direct"));
  void hasCourse;
  const statusOf = (u) => {
    if (u.archived) return { l: "Archiviato", c: "bg-zinc-500/15 text-zinc-600" };
    if (submitted(u)) return { l: "Modulo firmato", c: "bg-violet-500/15 text-violet-700" };
    if (u.enrollment?.unlocked) return { l: "Iscritti", c: "bg-blue-500/15 text-blue-600" };
    if (u.from_trial) return { l: "In prova", c: "bg-teal-500/15 text-teal-700" };
    return { l: "Registrati", c: "bg-slate-400/20 text-slate-600" };
  };
  const CollapsedBadges = ({ u }) => {
    const st = statusOf(u); const b = bucketOf(u);
    return (
      <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
        <Badge className={`${st.c} border-0`} data-testid={`status-badge-${u.user_id}`}>{st.l}</Badge>
        {PAY_BADGE[b] && <Badge className={`${PAY_BADGE[b].c} border-0`} data-testid={`pay-badge-${u.user_id}`}>{PAY_BADGE[b].l}</Badge>}
        {u.enrollment?.unlock_requested && !u.enrollment?.unlocked && <Badge className="bg-primary/15 text-primary border-0" data-testid={`unlock-requested-${u.user_id}`}>Sblocco richiesto</Badge>}
        <span className={`inline-flex items-center gap-1 text-xs font-medium ${u.enrollment?.arci_paid ? "text-green-600" : "text-muted-foreground"}`} data-testid={`arci-mini-${u.user_id}`}>{u.enrollment?.arci_paid ? <BadgeCheck size={13} /> : <Circle size={13} />} ARCI {u.enrollment?.arci_paid ? "✓ pagata" : "✗ non pagata"}</span>
      </div>
    );
  };
  const active = data.users.filter((u) => !u.archived);
  const counts = {
    all: active.length,
    from_trial: active.filter((u) => u.from_trial).length,
    direct: active.filter((u) => !u.from_trial && !submitted(u)).length,
    enrolled: active.filter((u) => submitted(u)).length,
    archived: data.users.filter((u) => u.archived).length,
  };
  const arciToPay = active.filter((u) => submitted(u) && !u.enrollment?.arci_paid).length;
  let shown;
  if (filter === "archived") shown = data.users.filter((u) => u.archived);
  else {
    shown = active;
    if (filter === "from_trial") shown = shown.filter((u) => u.from_trial);
    else if (filter === "direct") shown = shown.filter((u) => !u.from_trial && !submitted(u));
    else if (filter === "enrolled") shown = shown.filter((u) => submitted(u));
  }
  if (filter === "enrolled") {
    if (courseFilter !== "all") shown = shown.filter((u) => (u.enrollment?.course || "") === (courseFilter === "__none__" ? "" : courseFilter));
    if (payFilter !== "all") shown = shown.filter((u) => bucketOf(u) === payFilter);
    if (arciFilter !== "all") shown = shown.filter((u) => (arciFilter === "paid" ? !!u.enrollment?.arci_paid : !u.enrollment?.arci_paid));
  }
  const q = search.trim().toLowerCase();
  if (q) shown = shown.filter((u) => [u.name, u.email, u.phone].some((v) => (v || "").toLowerCase().includes(q)));

  const courseUsers = (c) => active.filter((u) => (u.enrollment?.course || "") === c);
  const courseStats = (c) => {
    const list = courseUsers(c);
    return {
      list,
      count: list.length,
      rataUnica: list.filter((u) => u.enrollment?.plan === "single").length,
      dueRate: list.filter((u) => u.enrollment?.plan === "installments").length,
      saldato: list.filter((u) => u.enrollment?.status === "enrolled").length,
      incassato: list.reduce((a, u) => a + paidOf(u), 0),
      arciPaid: list.filter((u) => u.enrollment?.arci_paid).length,
      arciNo: list.filter((u) => !u.enrollment?.arci_paid).length,
    };
  };

  const renderUserDetails = (u, courseActions = false) => {
    const e = u.enrollment; const status = e?.status || "locked"; const paid = paidOf(u);
    const hasPdf = !!e?.signed_at || ["form_signed", "partially_paid", "enrolled"].includes(status);
    const canPay = ["form_signed", "partially_paid"].includes(status) || (e?.unlocked && status === "unlocked");
    const otherCourses = (data.courses || []).filter((c) => c !== courseView);
    return (
      <>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <Input
                              data-testid={`name-input-${u.user_id}`}
                              value={nameEdits[u.user_id] ?? (u.name || "")}
                              onChange={(ev) => setNameEdits((s) => ({ ...s, [u.user_id]: ev.target.value }))}
                              className="h-7 text-xs bg-white"
                            />
                            <Button
                              size="sm" variant="outline"
                              data-testid={`save-name-${u.user_id}`}
                              disabled={((nameEdits[u.user_id] ?? (u.name || "")) || "").trim() === (u.name || "").trim()}
                              onClick={() => saveName(u.user_id, nameEdits[u.user_id] ?? (u.name || ""))}
                              className="h-7 px-2 shrink-0"
                            ><Save size={13} /></Button>
                          </div>
                          <div className="flex items-center gap-1.5 mt-1">
                            <Input
                              data-testid={`email-input-${u.user_id}`}
                              type="email"
                              value={emailEdits[u.user_id] ?? u.email}
                              onChange={(ev) => setEmailEdits((s) => ({ ...s, [u.user_id]: ev.target.value }))}
                              className="h-7 text-xs bg-white"
                            />
                            <Button
                              size="sm" variant="outline"
                              data-testid={`save-email-${u.user_id}`}
                              disabled={((emailEdits[u.user_id] ?? u.email) || "").trim().toLowerCase() === (u.email || "").toLowerCase()}
                              onClick={() => saveEmail(u.user_id, emailEdits[u.user_id] ?? u.email)}
                              className="h-7 px-2 shrink-0"
                            ><Save size={13} /></Button>
                          </div>
                          <div className="flex items-center gap-1.5 mt-1">
                            <Input
                              data-testid={`phone-input-${u.user_id}`}
                              type="tel"
                              value={phoneEdits[u.user_id] ?? (u.phone || "")}
                              onChange={(ev) => setPhoneEdits((s) => ({ ...s, [u.user_id]: ev.target.value }))}
                              placeholder="Telefono"
                              className="h-7 text-xs bg-white"
                            />
                            <Button
                              size="sm" variant="outline"
                              data-testid={`save-phone-${u.user_id}`}
                              disabled={((phoneEdits[u.user_id] ?? (u.phone || "")) || "").trim() === (u.phone || "").trim()}
                              onClick={() => savePhone(u.user_id, phoneEdits[u.user_id] ?? (u.phone || ""))}
                              className="h-7 px-2 shrink-0"
                            ><Save size={13} /></Button>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <Badge className={`${(ST[status] || ST.locked).c} border-0`}>{(ST[status] || ST.locked).l}</Badge>
                          {u.archived && <Badge className="bg-zinc-500/15 text-zinc-600 border-0" data-testid={`archived-badge-${u.user_id}`}>Archiviato</Badge>}
                          {!u.archived && (u.from_trial
                            ? <Badge className="bg-teal-500/15 text-teal-700 border-0" data-testid={`from-trial-badge-${u.user_id}`}>In prova</Badge>
                            : <Badge className="bg-slate-400/20 text-slate-600 border-0" data-testid={`direct-badge-${u.user_id}`}>Utente</Badge>)}
                          {e?.unlock_requested && !e?.unlocked && <Badge className="bg-primary/15 text-primary border-0" data-testid={`unlock-requested-${u.user_id}`}>Sblocco richiesto</Badge>}
                        </div>
                      </div>

                      {(u.trials || []).length > 0 && (
                        <div className="mb-3 rounded-lg bg-muted/40 border border-border p-2.5 text-xs" data-testid={`trials-${u.user_id}`}>
                          <p className="font-medium text-foreground mb-1.5 flex items-center gap-1"><Ticket size={13} /> Lezioni di prova prenotate</p>
                          <ul className="space-y-1">
                            {(u.trials || []).map((t, i) => (
                              <li key={i} className="text-muted-foreground">
                                {t.date ? new Date(t.date).toLocaleDateString("it-IT") : "—"}{t.time ? ` · ${t.time}` : ""}{t.location ? ` · ${t.location}` : ""}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {e?.note && e.note.trim() && (
                        <div className="mb-3 rounded-lg bg-amber-500/10 border border-amber-500/30 p-2.5 text-xs" data-testid={`note-${u.user_id}`}>
                          <p className="font-medium text-amber-700 mb-1 flex items-center gap-1"><FileText size={13} /> Nota dell'allievo</p>
                          <p className="text-muted-foreground whitespace-pre-line break-words">{e.note}</p>
                        </div>
                      )}

                      <div className="text-sm text-muted-foreground mb-3">{paid > 0 ? <>Pagato: <span className="text-foreground">€{paid.toFixed(2)}</span>{e?.amount_due > 0 && <span className="text-amber-600"> · Residuo €{e.amount_due.toFixed(2)}</span>}</> : "Nessun pagamento ancora"}</div>

                      {paid > 0 && (u.payments || []).length > 0 && (
                        <div className="mb-3 rounded-lg bg-muted/40 border border-border p-2.5 text-xs" data-testid={`pay-history-${u.user_id}`}>
                          <p className="font-medium text-foreground mb-1.5">Piano: {e?.plan === "single" ? "Rata unica" : e?.plan === "installments" ? "Due rate" : "—"}</p>
                          <ul className="space-y-1">
                            {(u.payments || []).slice().sort((a, b) => (a.created_at > b.created_at ? 1 : -1)).map((p, i, arr) => (
                              <li key={p.id || i} className="flex items-center justify-between gap-2 text-muted-foreground">
                                <span>{p.created_at ? new Date(p.created_at).toLocaleDateString("it-IT") : "—"} · {METHOD_L[p.method] || p.method || "—"} · {p.plan === "installments" ? `Rata ${i + 1} di ${Math.max(2, arr.length)}` : "Rata unica"}</span>
                                <span className="text-foreground shrink-0">€{(p.amount || 0).toFixed(2)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {e?.in_person_request && (
                        <div className="mb-3 rounded-lg bg-amber-500/10 border border-amber-500/30 p-2.5 text-xs text-amber-700" data-testid={`in-person-req-${u.user_id}`}>
                          <BadgeEuro size={13} className="inline mr-1 -mt-0.5" />
                          {e.in_person_request.declared_paid
                            ? <> <strong>Bonifico segnalato dall'allievo</strong>. Verifica l'accredito sul conto e registra il pagamento qui sotto.</>
                            : <> Vuole pagare <strong>in sede</strong>: {{ single: "Unica rata", installments: "Due rate" }[e.in_person_request.plan]} · {{ carta: "Bancomat/Carta", contanti: "Contanti", bonifico: "Bonifico", satispay: "Satispay" }[e.in_person_request.method]}. Registra il pagamento qui sotto.</>}
                        </div>
                      )}

                      <div className="mb-3">
                        <label className="text-xs text-muted-foreground flex items-center gap-1"><GraduationCap size={12} /> Corso</label>
                        <Select value={e?.course || "__none__"} onValueChange={(v) => setUserCourse(u.user_id, v)}>
                          <SelectTrigger data-testid={`course-${u.user_id}`} className="bg-white mt-1 h-9"><SelectValue placeholder="Nessun corso" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">Nessun corso</SelectItem>
                            {(data.courses || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="mb-3">
                        {e?.arci_paid ? (
                          <Button
                            size="sm"
                            data-testid={`arci-toggle-${u.user_id}`}
                            onClick={() => setArciRevert({ uid: u.user_id })}
                            className="bg-green-600 text-white hover:bg-green-700"
                          >
                            <BadgeCheck size={14} className="mr-1.5" /> Tessera ARCI pagata{e?.arci_method ? ` · ${ARCI_METHOD_L[e.arci_method] || e.arci_method}` : ""}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`arci-toggle-${u.user_id}`}
                            onClick={() => { setArciMethod(""); setArciPay({ uid: u.user_id }); }}
                            className="text-muted-foreground border-border hover:text-foreground"
                          >
                            <Circle size={14} className="mr-1.5" /> Tessera ARCI non pagata
                          </Button>
                        )}
                      </div>

                      <div className="mb-3">
                        <label className="text-xs text-muted-foreground flex items-center gap-1"><StickyNote size={12} /> Nota interna (solo master)</label>
                        <Textarea
                          data-testid={`master-note-${u.user_id}`}
                          rows={2}
                          value={noteEdits[u.user_id] ?? e?.master_note ?? ""}
                          onChange={(ev) => setNoteEdits((s) => ({ ...s, [u.user_id]: ev.target.value }))}
                          placeholder="Appunti visibili solo al master…"
                          className="bg-white mt-1 text-sm"
                        />
                        <Button
                          size="sm" variant="outline"
                          data-testid={`save-note-${u.user_id}`}
                          disabled={(noteEdits[u.user_id] ?? e?.master_note ?? "") === (e?.master_note ?? "")}
                          onClick={() => saveMasterNote(u.user_id, noteEdits[u.user_id] ?? e?.master_note ?? "")}
                          className="mt-1.5 h-8"
                        ><Save size={13} className="mr-1.5" /> Salva nota</Button>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {e?.unlocked ? (
                          <Button size="sm" variant="outline" data-testid={`lock-${u.user_id}`} onClick={() => toggle(u.user_id, false)}><Lock size={14} className="mr-1.5" /> Blocca</Button>
                        ) : (
                          <Button size="sm" data-testid={`unlock-${u.user_id}`} onClick={() => toggle(u.user_id, true)} className="bg-primary text-primary-foreground hover:bg-primary/90"><Unlock size={14} className="mr-1.5" /> Sblocca</Button>
                        )}
                        {hasPdf && <Button size="sm" variant="outline" data-testid={`dl-${u.user_id}`} onClick={() => dlPdf(u.user_id)}><Download size={14} className="mr-1.5" /> Modulo</Button>}
                        {e?.signed_at && paid === 0 && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`reject-${u.user_id}`} className="text-destructive hover:text-destructive border-destructive/40"><Eraser size={14} className="mr-1.5" /> Rifiuta firma</Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Rifiutare il modulo firmato?</AlertDialogTitle>
                                <AlertDialogDescription>Per {u.name || u.email}. Il modulo firmato verrà <strong>cancellato dal server</strong> e l'allievo riceverà una email che lo invita a firmare di nuovo (firma non leggibile). L'iscrizione resterà sbloccata così potrà rifirmare.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annulla</AlertDialogCancel>
                                <AlertDialogAction data-testid={`confirm-reject-${u.user_id}`} onClick={() => rejectForm(u.user_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Rifiuta e avvisa</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        {status !== "enrolled" && canPay && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`manual-pay-${u.user_id}`}><CreditCard size={14} className="mr-1.5" /> Segna pagato</Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Registra pagamento in sede</AlertDialogTitle>
                                <AlertDialogDescription>Per {u.name || u.email}. Scegli il metodo e cosa ha pagato.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <div className="space-y-3">
                                <Label className="text-xs">Metodo di pagamento</Label>
                                <Select value={payMethod} onValueChange={setPayMethod}>
                                  <SelectTrigger data-testid="pay-method" className="bg-white"><SelectValue /></SelectTrigger>
                                  <SelectContent>{METHODS.map((m) => <SelectItem key={m.k} value={m.k}>{m.l}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                              <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                                <AlertDialogCancel>Annulla</AlertDialogCancel>
                                {status === "partially_paid" ? (
                                  <AlertDialogAction data-testid={`pay-2-${u.user_id}`} onClick={() => manualPay(u.user_id, "installments")} className="bg-primary text-primary-foreground hover:bg-primary/90">Salda 2ª rata</AlertDialogAction>
                                ) : (
                                  <>
                                    <AlertDialogAction data-testid={`pay-inst-${u.user_id}`} onClick={() => manualPay(u.user_id, "installments")} className="bg-secondary text-secondary-foreground hover:bg-secondary/80">1ª rata</AlertDialogAction>
                                    <AlertDialogAction data-testid={`pay-full-${u.user_id}`} onClick={() => manualPay(u.user_id, "single")} className="bg-primary text-primary-foreground hover:bg-primary/90">Unica rata</AlertDialogAction>
                                  </>
                                )}
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        {paid > 0 && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`undo-pay-${u.user_id}`} className="text-amber-700 hover:text-amber-700 border-amber-500/40"><RotateCcw size={14} className="mr-1.5" /> Annulla pagamento</Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Annullare l'ultimo pagamento?</AlertDialogTitle>
                                <AlertDialogDescription>Per {u.name || u.email}. Verrà rimosso <strong>l'ultimo pagamento registrato</strong> (€{paid.toFixed(2)} totali). Se aveva pagato una sola rata l'iscrizione torna a "Modulo firmato"; se aveva saldato la 2ª rata torna a "Pagato in parte". Usa questa opzione se hai segnato per sbaglio un pagamento.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annulla</AlertDialogCancel>
                                <AlertDialogAction data-testid={`confirm-undo-pay-${u.user_id}`} onClick={() => undoPay(u.user_id)} className="bg-amber-600 text-white hover:bg-amber-700">Annulla pagamento</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        {u.archived ? (
                          <Button size="sm" variant="outline" data-testid={`unarchive-${u.user_id}`} onClick={() => setArchived(u.user_id, false)}><ArchiveRestore size={14} className="mr-1.5" /> Ripristina</Button>
                        ) : (
                          <Button size="sm" variant="outline" data-testid={`archive-${u.user_id}`} onClick={() => setArchived(u.user_id, true)}><Archive size={14} className="mr-1.5" /> Archivia</Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`del-user-${u.user_id}`} className="text-destructive hover:text-destructive hover:bg-destructive/10"><UserX size={14} className="mr-1.5" /> Elimina</Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Eliminare {u.name || u.email}?</AlertDialogTitle><AlertDialogDescription>Rimuove account, prenotazioni, iscrizioni e pagamenti di tutti gli anni. Irreversibile.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction data-testid={`confirm-del-${u.user_id}`} onClick={() => delUser(u.user_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Elimina</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
        {courseActions && courseView && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid={`course-actions-${u.user_id}`}>
            {otherCourses.length > 0 && (
              <Select value="" onValueChange={(v) => moveCourse(u.user_id, v)}>
                <SelectTrigger data-testid={`move-course-${u.user_id}`} className="bg-white h-8 w-auto min-w-[160px] text-xs"><SelectValue placeholder="Sposta in altro corso" /></SelectTrigger>
                <SelectContent>{otherCourses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`remove-course-${u.user_id}`} className="h-8 text-destructive hover:text-destructive border-destructive/40"><UserX size={14} className="mr-1.5" /> Rimuovi dal corso</Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Rimuovere {u.name || u.email} dal corso?</AlertDialogTitle>
                  <AlertDialogDescription>L'iscritto verrà rimosso dal corso "{courseView}". Il pagamento e gli altri dati restano invariati; potrai riassegnarlo quando vuoi.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction data-testid={`confirm-remove-course-${u.user_id}`} onClick={() => removeFromCourse(u.user_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Rimuovi</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header style={{ paddingTop: "env(safe-area-inset-top)" }} className="sticky top-0 z-30 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0"><img src={LOGO} alt="Areté Teatro" className="h-9 w-9 rounded-full bg-white p-0.5 object-contain shrink-0" /><span className="text-lg font-display text-white whitespace-nowrap">Areté <span className="hidden xs:inline sm:inline">Teatro · </span><span className="text-primary">Master</span></span></div>
          <div className="flex items-center gap-1 sm:gap-2">
            <Button data-testid="refresh-admin" variant="ghost" size="sm" onClick={() => { loadUsers(adminYear); loadRest(); }} className="text-white hover:bg-white/10 hover:text-white"><RefreshCw size={16} className="sm:mr-1" /> <span className="hidden sm:inline">Aggiorna</span></Button>
            <PushToggleButton className="text-white hover:bg-white/10 hover:text-white" />
            <ChangePasswordDialog className="text-white hover:bg-white/10 hover:text-white" />
            <InstallAppButton className="text-white hover:bg-white/10 hover:text-white" />
            <Button data-testid="logout-button" variant="ghost" size="sm" onClick={logout} className="text-white hover:bg-white/10 hover:text-white"><LogOut size={16} className="sm:mr-1" /> <span className="hidden sm:inline">Esci</span></Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 fade-up">
        <h1 className="text-4xl sm:text-5xl mb-2">Pannello del Master</h1>
        <p className="text-muted-foreground mb-8 text-lg">Gestisci iscrizioni, pagamenti, corsi e notifiche.</p>

        <input ref={importRef} type="file" accept=".json,application/json" className="hidden" data-testid="import-backup-input" onChange={importData} />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="users" data-testid="tab-users"><Users size={16} className="mr-2" /> Iscritti</TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses"><GraduationCap size={16} className="mr-2" /> Corsi</TabsTrigger>
            <TabsTrigger value="slots" data-testid="tab-slots"><CalendarDays size={16} className="mr-2" /> Date di prova</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar"><CalendarDays size={16} className="mr-2" /> Calendario</TabsTrigger>
            <TabsTrigger value="year" data-testid="tab-year"><BadgeEuro size={16} className="mr-2" /> Anno & Corsi</TabsTrigger>
            <TabsTrigger value="module" data-testid="tab-module"><FileText size={16} className="mr-2" /> Modulo & Privacy</TabsTrigger>
            <TabsTrigger value="notif" data-testid="tab-notif"><Bell size={16} className="mr-2" /> Notifiche</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
              <div className="w-full md:w-52">
                <label className="text-xs text-muted-foreground">Anno scolastico</label>
                <Select value={adminYear} onValueChange={(y) => loadUsers(y)}>
                  <SelectTrigger data-testid="admin-year-select" className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{data.years.map((y) => <SelectItem key={y} value={y}>{y}{y === data.current_year ? " (corrente)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="mb-4">
              <div className="relative w-full md:max-w-md">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome, cognome, email o telefono…" className="bg-white pl-9" />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
              {GROUPS.map((g) => (
                <button key={g.k} data-testid={`group-card-${g.k}`} onClick={() => { setFilter(g.k); setOpenCard(null); }}
                  className={`text-left rounded-xl border p-4 transition-colors ${filter === g.k ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"}`}>
                  <div className={`text-2xl font-semibold ${filter === g.k ? "text-primary-foreground" : "text-foreground"}`}>{counts[g.k] ?? 0}</div>
                  <div className={`text-sm ${filter === g.k ? "text-primary-foreground/90" : "text-muted-foreground"}`}>{g.l}</div>
                </button>
              ))}
            </div>

            {filter === "enrolled" && (
              <div className="mb-5 space-y-3 rounded-xl border border-border bg-card/50 p-4" data-testid="contextual-filters">
                <div className="flex items-center gap-2 pb-1" data-testid="arci-to-pay-counter">
                  <BadgeCheck size={16} className={arciToPay > 0 ? "text-amber-600" : "text-green-600"} />
                  <span className="text-sm">Tessere ARCI da incassare: <strong className={arciToPay > 0 ? "text-amber-600" : "text-green-600"}>{arciToPay}</strong></span>
                </div>
                <div className="w-full md:w-56">
                  <label className="text-xs text-muted-foreground">Filtra per corso</label>
                  <Select value={courseFilter} onValueChange={setCourseFilter}>
                    <SelectTrigger data-testid="course-filter" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i corsi</SelectItem>
                      <SelectItem value="__none__">Senza corso</SelectItem>
                      {(data.courses || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Pagamento</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {PAY_FILTERS.map((p) => (
                      <button key={p.k} data-testid={`pay-filter-${p.k}`} onClick={() => setPayFilter(p.k)} className={`px-3 py-1 rounded-full text-sm border transition-colors ${payFilter === p.k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{p.l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Tessera ARCI</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {ARCI_FILTERS.map((p) => (
                      <button key={p.k} data-testid={`arci-filter-${p.k}`} onClick={() => setArciFilter(p.k)} className={`px-3 py-1 rounded-full text-sm border transition-colors ${arciFilter === p.k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{p.l}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {shown.length === 0 ? (
              <Card className="p-8 bg-card border-border text-center text-muted-foreground">Nessun allievo in questa vista.</Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {shown.map((u) => {
                  const e = u.enrollment; const status = e?.status || "locked"; const paid = paidOf(u);
                  const hasPdf = !!e?.signed_at || ["form_signed", "partially_paid", "enrolled"].includes(status);
                  const canPay = ["form_signed", "partially_paid"].includes(status) || (e?.unlocked && status === "unlocked");
                  return (
                    <Card key={u.user_id} className="bg-card border-border overflow-hidden self-start" data-testid={`user-row-${u.user_id}`}>
                      <button type="button" data-testid={`user-toggle-${u.user_id}`} onClick={() => setOpenCard(openCard === u.user_id ? null : u.user_id)} className="w-full text-left p-4 flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-foreground font-medium truncate">{u.name || "—"}</p>
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                          <CollapsedBadges u={u} />
                        </div>
                        <ChevronDown size={18} className={`shrink-0 mt-1 text-muted-foreground transition-transform ${openCard === u.user_id ? "rotate-180" : ""}`} />
                      </button>
                      {openCard === u.user_id && (
                      <div className="px-5 pb-5 border-t border-border pt-4" data-testid={`user-details-${u.user_id}`}>
                      {renderUserDetails(u, false)}
                      </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="courses">
            {!courseView ? (
              <>
                <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
                  <div className="w-full md:w-52">
                    <label className="text-xs text-muted-foreground">Anno scolastico</label>
                    <Select value={adminYear} onValueChange={(y) => loadUsers(y)}>
                      <SelectTrigger data-testid="courses-year-select" className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>{data.years.map((y) => <SelectItem key={y} value={y}>{y}{y === data.current_year ? " (corrente)" : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {(data.courses || []).length === 0 ? (
                  <Card className="p-8 bg-card border-border text-center text-muted-foreground">Nessun corso configurato per quest'anno. Aggiungili nella scheda <strong>Anno &amp; Corsi</strong>.</Card>
                ) : (
                  <>
                  <CoursesSummary users={active.filter((u) => (data.courses || []).includes(u.enrollment?.course || ""))} paidOf={paidOf} />
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(data.courses || []).map((c) => {
                      const s = courseStats(c);
                      return (
                        <button key={c} data-testid={`course-card-${c}`} onClick={() => { setCourseView(c); setCoursePay("all"); setCourseArci("all"); }}
                          className="text-left rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors">
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <h3 className="text-lg font-medium flex items-start gap-2 min-w-0"><GraduationCap size={18} className="text-primary shrink-0 mt-0.5" /> <span className="break-words">{c}</span></h3>
                            <Badge className="bg-primary/15 text-primary border-0 shrink-0">{s.count} iscritti</Badge>
                          </div>
                          <div className="mb-3 flex flex-wrap gap-1.5">
                            <p data-testid={`course-visibility-${c}`} className={`text-xs font-medium inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ${calVisible[c]?.student_visible ? "bg-green-500/15 text-green-700" : "bg-muted text-muted-foreground"}`}>
                              {calVisible[c]?.student_visible ? <Eye size={13} /> : <EyeOff size={13} />} {calVisible[c]?.student_visible ? "Calendario visibile agli allievi" : "Calendario non visibile agli allievi"}
                            </p>
                            <p data-testid={`course-lock-${c}`} className={`text-xs font-medium inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ${calVisible[c]?.calendar_locked ? "bg-foreground text-background" : "border border-border text-muted-foreground"}`}>
                              {calVisible[c]?.calendar_locked ? <Lock size={13} /> : <Unlock size={13} />} {calVisible[c]?.calendar_locked ? "Calendario bloccato" : "Calendario modificabile"}
                            </p>
                          </div>
                          <div className="space-y-1.5 text-sm text-muted-foreground">
                            <div className="flex justify-between"><span>Rata unica</span><span className="text-foreground">{s.rataUnica}</span></div>
                            <div className="flex justify-between"><span>Due rate</span><span className="text-foreground">{s.dueRate}</span></div>
                            <div className="flex justify-between"><span>Saldato</span><span className="text-foreground">{s.saldato} · <span className="text-green-600">€{s.incassato.toFixed(2)}</span></span></div>
                            <div className="flex justify-between"><span>Tessera ARCI</span><span className="text-foreground"><span className="text-green-600">{s.arciPaid}</span> pagata · <span className="text-muted-foreground">{s.arciNo}</span> no</span></div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  </>
                )}
              </>
            ) : (() => {
              const s = courseStats(courseView);
              let list = s.list;
              if (coursePay !== "all") list = list.filter((u) => bucketOf(u) === coursePay);
              if (courseArci !== "all") list = list.filter((u) => (courseArci === "paid" ? !!u.enrollment?.arci_paid : !u.enrollment?.arci_paid));
              const candidates = active.filter((u) => !(u.enrollment?.course));
              const cq = addQ.trim().toLowerCase();
              const filteredCandidates = cq ? candidates.filter((u) => [u.name, u.email].some((v) => (v || "").toLowerCase().includes(cq))) : candidates;
              return (
                <>
                  <button data-testid="courses-back" onClick={() => setCourseView(null)} className="text-sm text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1">← Torna ai corsi</button>
                  <Card className="p-5 bg-card border-border mb-5">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <h2 className="text-2xl flex items-start gap-2 min-w-0"><GraduationCap size={20} className="text-primary shrink-0 mt-1" /> <span className="break-words">{courseView}</span></h2>
                      <Badge className="bg-primary/15 text-primary border-0 shrink-0">{s.count} iscritti</Badge>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div className="rounded-lg bg-muted/40 p-3"><p className="text-muted-foreground text-xs">Rata unica</p><p className="text-lg text-foreground">{s.rataUnica}</p></div>
                      <div className="rounded-lg bg-muted/40 p-3"><p className="text-muted-foreground text-xs">Due rate</p><p className="text-lg text-foreground">{s.dueRate}</p></div>
                      <div className="rounded-lg bg-muted/40 p-3"><p className="text-muted-foreground text-xs">Saldato · Incassato</p><p className="text-lg text-foreground">{s.saldato} · <span className="text-green-600">€{s.incassato.toFixed(2)}</span></p></div>
                      <div className="rounded-lg bg-muted/40 p-3"><p className="text-muted-foreground text-xs">Tessera ARCI</p><p className="text-lg text-foreground"><span className="text-green-600">{s.arciPaid}</span> · {s.arciNo}</p></div>
                    </div>
                  </Card>

                  <CourseCalendar key={`${adminYear}|${courseView}`} year={adminYear} course={courseView} onMeta={(m) => setCalVisible((s) => ({ ...s, [m.course]: m }))} />

                  <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Pagamento</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {PAY_FILTERS.map((p) => (
                            <button key={p.k} data-testid={`course-pay-filter-${p.k}`} onClick={() => setCoursePay(p.k)} className={`px-3 py-1 rounded-full text-sm border transition-colors ${coursePay === p.k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{p.l}</button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Tessera ARCI</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {ARCI_FILTERS.map((p) => (
                            <button key={p.k} data-testid={`course-arci-filter-${p.k}`} onClick={() => setCourseArci(p.k)} className={`px-3 py-1 rounded-full text-sm border transition-colors ${courseArci === p.k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{p.l}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <Button data-testid="course-add-open" onClick={() => { setAddQ(""); setAddOpen(true); }} className="bg-primary text-primary-foreground hover:bg-primary/90"><Plus size={16} className="mr-1.5" /> Aggiungi iscritto</Button>
                  </div>

                  {list.length === 0 ? (
                    <Card className="p-8 bg-card border-border text-center text-muted-foreground">Nessun iscritto in questa vista.</Card>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {list.map((u) => {
                        return (
                          <Card key={u.user_id} className="bg-card border-border overflow-hidden self-start" data-testid={`course-user-${u.user_id}`}>
                            <button type="button" data-testid={`user-toggle-${u.user_id}`} onClick={() => setOpenCard(openCard === u.user_id ? null : u.user_id)} className="w-full text-left p-4 flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <p className="text-foreground font-medium truncate">{u.name || "—"}</p>
                                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                                <CollapsedBadges u={u} />
                              </div>
                              <ChevronDown size={18} className={`shrink-0 mt-1 text-muted-foreground transition-transform ${openCard === u.user_id ? "rotate-180" : ""}`} />
                            </button>
                            {openCard === u.user_id && (
                              <div className="px-5 pb-5 border-t border-border pt-4" data-testid={`user-details-${u.user_id}`}>
                                {renderUserDetails(u, true)}
                              </div>
                            )}
                          </Card>
                        );
                      })}
                    </div>
                  )}

                  <Dialog open={addOpen} onOpenChange={setAddOpen}>
                    <DialogContent className="max-w-lg" data-testid="course-add-dialog">
                      <DialogHeader><DialogTitle>Aggiungi iscritto a "{courseView}"</DialogTitle></DialogHeader>
                      <div className="relative">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input data-testid="course-add-search" value={addQ} onChange={(e) => setAddQ(e.target.value)} placeholder="Cerca per nome o email…" className="bg-white pl-9" />
                      </div>
                      <div className="max-h-80 overflow-y-auto divide-y divide-border">
                        {filteredCandidates.length === 0 ? (
                          <p className="text-sm text-muted-foreground py-6 text-center">Nessun utente senza corso disponibile.</p>
                        ) : filteredCandidates.map((u) => (
                          <div key={u.user_id} className="flex items-center justify-between gap-2 py-2.5" data-testid={`candidate-${u.user_id}`}>
                            <div className="min-w-0">
                              <p className="text-sm text-foreground truncate">{u.name || "—"}</p>
                              <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                            </div>
                            <Button size="sm" data-testid={`add-candidate-${u.user_id}`} onClick={() => addToCourse(u.user_id, courseView)} className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"><Plus size={14} className="mr-1" /> Aggiungi</Button>
                          </div>
                        ))}
                      </div>
                    </DialogContent>
                  </Dialog>
                </>
              );
            })()}
          </TabsContent>

          <TabsContent value="slots">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="p-6 bg-card border-border lg:col-span-1 h-fit min-w-0">
                <h3 className="text-xl mb-4">Nuova data</h3>
                <form onSubmit={addSlot} className="space-y-3">
                  <div><Label className="text-xs">Data</Label><Input data-testid="slot-date" type="date" required value={slot.date} onChange={(e) => setSlot({ ...slot, date: e.target.value })} className="mt-1 bg-white" /></div>
                  <div><Label className="text-xs">Orario</Label><Input data-testid="slot-time" required placeholder="18:00" value={slot.time} onChange={(e) => setSlot({ ...slot, time: e.target.value })} className="mt-1 bg-white" /></div>
                  <div><Label className="text-xs">Luogo</Label><Input data-testid="slot-location" placeholder="Sala prove" value={slot.location} onChange={(e) => setSlot({ ...slot, location: e.target.value })} className="mt-1 bg-white" /></div>
                  <div><Label className="text-xs">Posti</Label><Input data-testid="slot-capacity" type="number" min={1} value={slot.capacity} onChange={(e) => setSlot({ ...slot, capacity: e.target.value })} className="mt-1 bg-white" /></div>
                  <Button data-testid="add-slot" className="w-full bg-primary text-primary-foreground hover:bg-primary/90"><Plus size={16} className="mr-1" /> Aggiungi data</Button>
                </form>
              </Card>
              <div className="lg:col-span-2 space-y-3 min-w-0">
                {slots.length === 0 && <Card className="p-8 bg-card border-border text-center text-muted-foreground">Nessuna data inserita.</Card>}
                {slots.map((s) => {
                  const bk = bookingsFor(s.id);
                  const full = s.booked_count >= s.capacity;
                  return (
                    <Card key={s.id} className="p-5 bg-card border-border" data-testid={`slot-row-${s.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium capitalize">{new Date(s.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })} · {s.time}</p>
                          <p className="text-sm text-muted-foreground">{s.location || "—"} · <span className="text-foreground">{s.booked_count}/{s.capacity}</span> prenotati</p>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {s.closed ? <Badge className="bg-destructive/15 text-destructive border-0">Prenotazioni chiuse</Badge>
                              : full ? <Badge className="bg-amber-500/15 text-amber-600 border-0">Capienza raggiunta</Badge>
                              : <Badge className="bg-green-500/15 text-green-600 border-0">Prenotazioni aperte</Badge>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="icon" variant="ghost" data-testid={`edit-slot-${s.id}`} onClick={() => setEditSlot({ ...s })} title="Modifica"><Pencil size={15} /></Button>
                          <Button size="icon" variant="ghost" data-testid={`toggle-slot-${s.id}`} onClick={() => toggleSlotClosed(s)} title={s.closed ? "Riapri prenotazioni" : "Chiudi prenotazioni"} className={s.closed ? "text-green-600 hover:text-green-600" : "text-amber-600 hover:text-amber-600"}>{s.closed ? <Unlock size={15} /> : <Lock size={15} />}</Button>
                          <Button size="icon" variant="ghost" data-testid={`del-slot-${s.id}`} onClick={() => delSlot(s.id)} className="text-destructive hover:text-destructive" title="Elimina"><Trash2 size={16} /></Button>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-border/50 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-muted-foreground">{bk.length} iscritti a questa lezione</p>
                        <Button size="sm" variant="outline" data-testid={`export-slot-${s.id}`} onClick={() => downloadAttendees(s)}><FileDown size={14} className="mr-1.5" /> Scarica lista (CSV)</Button>
                      </div>
                      {bk.length > 0 && <div className="mt-3 pt-3 border-t border-border/50 space-y-1.5">{bk.map((b) => (
                        <div key={b.id} className="flex items-center justify-between gap-2 min-w-0" data-testid={`booking-${b.id}`}>
                          <div className="text-xs min-w-0 break-words leading-snug">
                            <p className="text-foreground font-medium">{b.user_name || "—"}</p>
                            <p className="text-muted-foreground">{[b.user_email, b.user_phone].filter(Boolean).join(" · ")}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button size="sm" variant="ghost" data-testid={`move-booking-${b.id}`} onClick={() => { setMoveBk(b); setMoveTarget(""); }} className="h-7 px-2 text-xs text-primary hover:text-primary hover:bg-primary/10"><CalendarClock size={13} className="mr-1" /> Sposta</Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`cancel-booking-${b.id}`} className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"><Trash2 size={13} className="mr-1" /> Cancella</Button></AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader><AlertDialogTitle>Cancellare la prenotazione?</AlertDialogTitle><AlertDialogDescription>Prenotazione di {b.user_name || b.user_email} per questa lezione di prova. La persona potrà prenotarne un'altra.</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction data-testid={`confirm-cancel-booking-${b.id}`} onClick={() => cancelBooking(b.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Cancella</AlertDialogAction></AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      ))}</div>}
                    </Card>
                  );
                })}
              </div>
            </div>

            <Dialog open={!!editSlot} onOpenChange={(o) => { if (!o) setEditSlot(null); }}>
              <DialogContent className="max-w-md" data-testid="edit-slot-dialog">
                <DialogHeader><DialogTitle>Modifica lezione di prova</DialogTitle></DialogHeader>
                {editSlot && (
                  <div className="space-y-3">
                    <div><Label className="text-xs">Data</Label><Input data-testid="edit-slot-date" type="date" value={(editSlot.date || "").slice(0, 10)} onChange={(e) => setEditSlot({ ...editSlot, date: e.target.value })} className="mt-1 bg-white" /></div>
                    <div><Label className="text-xs">Orario</Label><Input data-testid="edit-slot-time" value={editSlot.time} onChange={(e) => setEditSlot({ ...editSlot, time: e.target.value })} className="mt-1 bg-white" placeholder="18:00" /></div>
                    <div><Label className="text-xs">Luogo</Label><Input data-testid="edit-slot-location" value={editSlot.location || ""} onChange={(e) => setEditSlot({ ...editSlot, location: e.target.value })} className="mt-1 bg-white" placeholder="Sala prove" /></div>
                    <div><Label className="text-xs">Posti (capienza max)</Label><Input data-testid="edit-slot-capacity" type="number" min={editSlot.booked_count || 1} value={editSlot.capacity} onChange={(e) => setEditSlot({ ...editSlot, capacity: e.target.value })} className="mt-1 bg-white" /><p className="text-xs text-muted-foreground mt-1">Già prenotati: {editSlot.booked_count}. Al raggiungimento della capienza le prenotazioni si bloccano automaticamente.</p></div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditSlot(null)}>Annulla</Button>
                  <Button data-testid="edit-slot-save" onClick={saveEditSlot} className="bg-primary text-primary-foreground hover:bg-primary/90"><Save size={15} className="mr-1.5" /> Salva modifiche</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={!!moveBk} onOpenChange={(o) => { if (!o) { setMoveBk(null); setMoveTarget(""); } }}>
              <DialogContent className="max-w-md overflow-hidden" data-testid="move-booking-dialog">
                <DialogHeader><DialogTitle>Sposta la lezione di prova</DialogTitle></DialogHeader>
                {moveBk && (
                  <div className="space-y-3 min-w-0">
                    <p className="text-sm text-muted-foreground">Prenotazione di <strong className="text-foreground">{moveBk.user_name || moveBk.user_email}</strong>. Scegli la nuova data: l'allievo riceverà un'email di aggiornamento.</p>
                    <div className="min-w-0">
                      <Label className="text-xs">Nuova data di prova</Label>
                      <Select value={moveTarget} onValueChange={setMoveTarget}>
                        <SelectTrigger data-testid="move-target-select" className="bg-white mt-1 w-full min-w-0 [&>span]:min-w-0 [&>span]:truncate [&>span]:block"><SelectValue placeholder="Seleziona una data" /></SelectTrigger>
                        <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                          {slots.filter((s) => s.id !== moveBk.slot_id).map((s) => (
                            <SelectItem key={s.id} value={s.id} className="whitespace-normal break-words pr-8">
                              {new Date(s.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })} · {s.time}{s.location ? ` · ${s.location}` : ""} ({s.booked_count}/{s.capacity})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setMoveBk(null); setMoveTarget(""); }}>Annulla</Button>
                  <Button data-testid="move-booking-save" onClick={doMoveBooking} className="bg-primary text-primary-foreground hover:bg-primary/90"><CalendarClock size={15} className="mr-1.5" /> Sposta prenotazione</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="calendar">
            <SchoolCalendar year={calYear || adminYear} years={data.years} currentYear={data.current_year} onYear={setCalYear} />
          </TabsContent>

          <TabsContent value="year">
            {!yearCfg ? <p className="text-muted-foreground">Caricamento…</p> : (
              <div className="space-y-6 max-w-2xl">
                <Card className="p-6 bg-card border-border space-y-4">
                  <h3 className="text-xl">Anno scolastico</h3>
                  <p className="text-sm text-muted-foreground">Seleziona un anno esistente o scrivi un nuovo anno (es. 2027/2028) per crearlo. Prezzi e corsi sono indipendenti per ogni anno.</p>
                  <div className="flex flex-wrap gap-2 items-end">
                    <div>
                      <Label className="text-xs">Anno esistente</Label>
                      <Select value={data.years.includes(yearCfgYear) ? yearCfgYear : ""} onValueChange={(y) => setYearCfgYear(y)}>
                        <SelectTrigger data-testid="year-cfg-select" className="bg-white w-40 mt-1"><SelectValue placeholder="Scegli" /></SelectTrigger>
                        <SelectContent>{data.years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Oppure nuovo anno</Label>
                      <Input data-testid="year-cfg-input" value={yearCfgYear} onChange={(e) => setYearCfgYear(e.target.value)} placeholder="2027/2028" className="bg-white w-40 mt-1" />
                    </div>
                  </div>
                </Card>
                <Card className="p-6 bg-card border-border space-y-4">
                  <h3 className="text-xl">Prezzi (anno {yearCfgYear})</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="text-xs">Rata unica (€)</Label><Input data-testid="year-full" type="number" value={yearCfg.full_amount} onChange={(e) => setYearCfg({ ...yearCfg, full_amount: e.target.value })} className="mt-1 bg-white" /></div>
                    <div><Label className="text-xs">Importo singola rata (€) — pagata due volte</Label><Input data-testid="year-inst" type="number" value={yearCfg.installment_amount} onChange={(e) => setYearCfg({ ...yearCfg, installment_amount: e.target.value })} className="mt-1 bg-white" /></div>
                  </div>
                </Card>
                <Card className="p-6 bg-card border-border space-y-4">
                  <h3 className="text-xl">Promemoria seconda rata</h3>
                  <p className="text-sm text-muted-foreground">Il giorno di invio è la data da cui il promemoria della 2ª rata viene inviato automaticamente a tutti gli allievi con piano "due rate" ancora da saldare. La scadenza compare nel testo dell'email (variabile <code>{"{scadenza}"}</code>). Il testo si modifica nella scheda Notifiche.</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="text-xs">Giorno di invio promemoria</Label><Input data-testid="year-inst-start" type="date" value={(yearCfg.installment_start || "").slice(0, 10)} onChange={(e) => setYearCfg({ ...yearCfg, installment_start: e.target.value })} className="mt-1 bg-white" /></div>
                    <div><Label className="text-xs">Scadenza pagamento 2ª rata</Label><Input data-testid="year-inst-deadline" type="date" value={(yearCfg.installment_deadline || "").slice(0, 10)} onChange={(e) => setYearCfg({ ...yearCfg, installment_deadline: e.target.value })} className="mt-1 bg-white" /></div>
                  </div>
                </Card>
                <Card className="p-6 bg-card border-border space-y-3">
                  <h3 className="text-xl">Corsi dell'anno</h3>
                  <p className="text-sm text-muted-foreground">Aggiungi o rinomina i corsi. Rinominando un corso, gli iscritti già assegnati vengono aggiornati automaticamente al nuovo nome. Trascina per riordinare.</p>
                  <div className="space-y-2">
                    {courseRows.map((r, i) => (
                      <div
                        key={i}
                        data-testid={`course-row-${i}`}
                        className={`flex items-center gap-2 rounded-lg transition-colors ${dragIdx === i ? "opacity-50" : ""} ${overIdx === i && dragIdx !== null && dragIdx !== i ? "ring-2 ring-primary/50" : ""}`}
                        draggable
                        onDragStart={() => setDragIdx(i)}
                        onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
                        onDrop={(e) => { e.preventDefault(); moveCourseRow(dragIdx, i); setDragIdx(null); setOverIdx(null); }}
                        onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                      >
                        <span data-testid={`course-drag-${i}`} className="cursor-grab active:cursor-grabbing text-muted-foreground shrink-0 touch-none" title="Trascina per riordinare"><GripVertical size={16} /></span>
                        <Input data-testid={`course-input-${i}`} value={r.name} onChange={(e) => setCourseRows((rows) => rows.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} className="bg-white" placeholder="Nome del corso" />
                        <Button size="icon" variant="ghost" data-testid={`course-remove-${i}`} onClick={() => setCourseRows((rows) => rows.filter((_, idx) => idx !== i))} className="text-destructive hover:text-destructive shrink-0"><Trash2 size={15} /></Button>
                      </div>
                    ))}
                    {courseRows.length === 0 && <p className="text-sm text-muted-foreground">Nessun corso. Aggiungine uno qui sotto.</p>}
                  </div>
                  <Button size="sm" variant="outline" data-testid="course-add-row" onClick={() => setCourseRows((rows) => [...rows, { orig: "", name: "" }])}><Plus size={14} className="mr-1" /> Aggiungi corso</Button>
                </Card>
                <CourseMetaSection ref={metaRef} year={yearCfg.year} courses={yearCfg.courses || []} />
                <ClosuresSection year={yearCfg.year} />
                <div className="pt-2 pb-4">
                  <Button data-testid="year-save" onClick={saveYear} disabled={savingYear} size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90"><Save size={16} className="mr-2" /> {savingYear ? "Salvataggio…" : "Salva anno e corsi"}</Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="module">
            {!cfg ? <p className="text-muted-foreground">Caricamento…</p> : (
              <div className="space-y-6 max-w-3xl">
                <Card className="p-6 bg-card border-border space-y-3">
                  <h3 className="text-xl">Codice di iscrizione</h3>
                  <p className="text-sm text-muted-foreground">Comunica questo codice agli allievi al termine della lezione di prova. Inserendolo nell'area personale, potranno sbloccare da soli l'iscrizione per l'anno <strong>{adminYear}</strong>. Non è visibile agli utenti.</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input data-testid="enroll-code-input" value={enrollCode} onChange={(e) => setEnrollCode(e.target.value)} placeholder="Es. TEATRO2027" className="bg-white" />
                    <Button data-testid="save-enroll-code" onClick={saveEnrollCode} className="shrink-0"><Save size={15} className="mr-1.5" /> Salva codice</Button>
                  </div>
                </Card>
                <Card className="p-6 bg-card border-border space-y-4">
                  <h3 className="text-xl">Contenuto del modulo di iscrizione</h3>
                  <p className="text-sm text-muted-foreground">Gli allievi lo leggeranno, accetteranno (X), firmeranno col dito e riceveranno il PDF firmato.</p>
                  <div><Label className="text-xs">Titolo del modulo *</Label><Input data-testid="cfg-title" value={cfg.title} onChange={(e) => setCfg({ ...cfg, title: e.target.value })} className="mt-1 bg-white" /></div>
                  <div><Label className="text-xs">Testo / regolamento / diciture</Label><Textarea data-testid="cfg-body" rows={7} value={cfg.body} onChange={(e) => setCfg({ ...cfg, body: e.target.value })} className="mt-1 bg-white" /></div>
                </Card>
                <Card className="p-6 bg-card border-border space-y-3">
                  <div className="flex items-center justify-between"><h3 className="text-xl">Campi da compilare</h3><Button size="sm" variant="outline" data-testid="cfg-add-field" onClick={addField}><Plus size={14} className="mr-1" /> Aggiungi campo</Button></div>
                  <div className="space-y-2">
                    {cfg.fields.map((f, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`cfg-field-${i}`}>
                        <Input value={f.label} onChange={(e) => setField(i, { label: e.target.value })} className="bg-white" placeholder="Etichetta campo" data-testid={`cfg-field-label-${i}`} />
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0 cursor-pointer"><Checkbox checked={f.required} onCheckedChange={(v) => setField(i, { required: !!v })} data-testid={`cfg-field-req-${i}`} /> Obbl.</label>
                        <Button size="icon" variant="ghost" onClick={() => removeField(i)} data-testid={`cfg-field-remove-${i}`} className="text-destructive hover:text-destructive shrink-0"><Trash2 size={15} /></Button>
                      </div>
                    ))}
                  </div>
                </Card>
                <Card className="p-6 bg-card border-border space-y-3">
                  <h3 className="text-xl">Informativa Privacy (GDPR)</h3>
                  <Textarea data-testid="cfg-privacy" rows={8} value={cfg.privacy_text} onChange={(e) => setCfg({ ...cfg, privacy_text: e.target.value })} className="bg-white" />
                </Card>
                <div className="flex flex-wrap gap-2">
                  <Button data-testid="cfg-save" onClick={saveCfg} disabled={savingCfg} className="bg-primary text-primary-foreground hover:bg-primary/90"><Save size={16} className="mr-2" /> {savingCfg ? "Salvataggio…" : "Salva modulo"}</Button>
                  <Button data-testid="cfg-reset" onClick={resetCfg} variant="outline"><RotateCcw size={16} className="mr-2" /> Ripristina predefinito</Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="notif">
            {!notif ? <p className="text-muted-foreground">Caricamento…</p> : (
              <div className="space-y-6 max-w-2xl">
                <Card className="p-6 bg-card border-border space-y-4">
                  <div className="flex items-center justify-between"><h3 className="text-xl">Promemoria lezione di prova</h3><Button size="sm" variant="outline" data-testid="add-reminder" onClick={addReminder}><Plus size={14} className="mr-1" /> Aggiungi</Button></div>
                  <p className="text-sm text-muted-foreground">Puoi impostare più promemoria (es. una settimana prima e il giorno prima). Per ognuno puoi personalizzare oggetto e testo. Variabili disponibili: <code>{"{nome}"}</code>, <code>{"{data}"}</code>, <code>{"{ora}"}</code>, <code>{"{luogo}"}</code>. Se lasci i campi vuoti verrà usato il testo predefinito.</p>
                  {(notif.reminders || []).length === 0 && <p className="text-sm text-muted-foreground">Nessun promemoria impostato.</p>}
                  {(notif.reminders || []).map((r, i) => (
                    <div key={i} className="border border-border rounded-lg p-4 space-y-3" data-testid={`reminder-${i}`}>
                      <div className="flex items-center gap-2">
                        <Input type="number" min={0} value={r.value} onChange={(e) => setReminder(i, { value: Number(e.target.value) })} className="bg-white w-24" data-testid={`reminder-value-${i}`} />
                        <Select value={r.unit} onValueChange={(v) => setReminder(i, { unit: v })}>
                          <SelectTrigger className="bg-white w-40" data-testid={`reminder-unit-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="giorni">giorni prima</SelectItem><SelectItem value="ore">ore prima</SelectItem></SelectContent>
                        </Select>
                        <Button size="icon" variant="ghost" onClick={() => removeReminder(i)} data-testid={`reminder-remove-${i}`} className="text-destructive hover:text-destructive shrink-0 ml-auto"><Trash2 size={15} /></Button>
                      </div>
                      <div><Label className="text-xs">Oggetto email</Label><Input value={r.subject || ""} onChange={(e) => setReminder(i, { subject: e.target.value })} placeholder="Promemoria: la tua lezione di prova" className="bg-white mt-1" data-testid={`reminder-subject-${i}`} /></div>
                      <div><Label className="text-xs">Testo email</Label><Textarea rows={3} value={r.body || ""} onChange={(e) => setReminder(i, { body: e.target.value })} placeholder={"Ciao {nome},\nti ricordiamo la tua lezione di prova di {data} alle {ora}{luogo}."} className="bg-white mt-1" data-testid={`reminder-body-${i}`} /></div>
                    </div>
                  ))}
                </Card>
                <Card className="p-6 bg-card border-border space-y-3">
                  <label className="flex items-center justify-between cursor-pointer">
                    <h3 className="text-xl">Promemoria seconda rata</h3>
                    <Checkbox checked={notif.installment_reminder?.enabled ?? true} onCheckedChange={(v) => setNotif({ ...notif, installment_reminder: { ...(notif.installment_reminder || {}), enabled: !!v } })} data-testid="installment-enabled" />
                  </label>
                  <p className="text-sm text-muted-foreground">Inviato automaticamente a tutti gli allievi con piano "due rate" ancora da saldare, a partire dalla data impostata nella scheda <strong>Anno &amp; Corsi</strong>. Variabili: <code>{"{nome}"}</code>, <code>{"{anno}"}</code>, <code>{"{importo}"}</code>, <code>{"{scadenza}"}</code>.</p>
                  <div><Label className="text-xs">Oggetto email</Label><Input value={notif.installment_reminder?.subject || ""} onChange={(e) => setNotif({ ...notif, installment_reminder: { ...(notif.installment_reminder || {}), subject: e.target.value } })} placeholder="Promemoria: seconda rata iscrizione" className="bg-white mt-1" data-testid="installment-subject" /></div>
                  <div><Label className="text-xs">Testo email</Label><Textarea rows={3} value={notif.installment_reminder?.body || ""} onChange={(e) => setNotif({ ...notif, installment_reminder: { ...(notif.installment_reminder || {}), body: e.target.value } })} placeholder={"Ciao {nome},\nti ricordiamo che la seconda rata dell'iscrizione {anno} (€{importo}) è da versare entro il {scadenza}."} className="bg-white mt-1" data-testid="installment-body" /></div>
                </Card>
                <Card className="p-6 bg-card border-border space-y-3">
                  <h3 className="text-xl">Email automatiche attive</h3>
                  {[{ k: "trial_confirmation", l: "Conferma prenotazione lezione di prova" }, { k: "trial_reminder", l: "Promemoria lezione di prova" }, { k: "unlock_notification", l: "Avviso quando l'iscrizione viene sbloccata" }, { k: "payment_confirmation", l: "Conferma di pagamento ricevuto" }].map((it) => (
                    <label key={it.k} className="flex items-center gap-3 cursor-pointer py-1"><Checkbox checked={notif[it.k]} onCheckedChange={(v) => setNotif({ ...notif, [it.k]: !!v })} data-testid={`notif-${it.k}`} /><span className="text-sm">{it.l}</span></label>
                  ))}
                </Card>
                <Button data-testid="notif-save" onClick={saveNotif} disabled={savingNotif} className="bg-primary text-primary-foreground hover:bg-primary/90"><Save size={16} className="mr-2" /> {savingNotif ? "Salvataggio…" : "Salva notifiche"}</Button>
                <p className="text-xs text-muted-foreground">Mittente: info@areteteatro.com. Ogni email include la firma della direzione.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <Collapsible className="mt-12 border-t border-border pt-6" data-testid="advanced-tools">
          <CollapsibleTrigger className="group flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="advanced-tools-trigger">
            <Settings2 size={16} /> <span className="font-medium">Strumenti avanzati</span>
            <ChevronDown size={16} className="transition-transform group-data-[state=open]:rotate-180" />
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-4">
            <div className="flex flex-wrap items-center gap-2" data-testid="backup-bar">
              <Button variant="outline" size="sm" data-testid="export-backup" onClick={exportData}><Download size={14} className="mr-1.5" /> Esporta backup iscritti</Button>
              <Button variant="outline" size="sm" data-testid="export-csv" onClick={exportCsv}><FileDown size={14} className="mr-1.5" /> Esporta iscritti (CSV)</Button>
              <Button variant="outline" size="sm" data-testid="import-backup" onClick={() => importRef.current?.click()}><Upload size={14} className="mr-1.5" /> Importa backup</Button>
              <span className="text-xs text-muted-foreground">Il backup include tutti gli iscritti e i loro dati. L'importazione aggiunge/aggiorna soltanto, non elimina nulla.</span>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </main>

      <Dialog open={!!arciPay} onOpenChange={(o) => { if (!o) { setArciPay(null); setArciMethod(""); } }}>
        <DialogContent className="max-w-sm" data-testid="arci-pay-dialog">
          <DialogHeader><DialogTitle>Confermi che la tessera ARCI è stata pagata?</DialogTitle></DialogHeader>
          <div>
            <p className="text-sm font-medium mb-2">Metodo di pagamento</p>
            <div className="grid grid-cols-3 gap-2">
              {ARCI_METHODS.map((m) => (
                <Button key={m.k} type="button" variant={arciMethod === m.k ? "default" : "outline"} data-testid={`arci-method-${m.k}`} onClick={() => setArciMethod(m.k)} className={`h-auto py-2 text-xs ${arciMethod === m.k ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`}>{m.l}</Button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setArciPay(null); setArciMethod(""); }}>Annulla</Button>
            <Button data-testid="arci-pay-confirm" disabled={!arciMethod} onClick={confirmArciPaid} className="bg-green-600 text-white hover:bg-green-700">Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!arciRevert} onOpenChange={(o) => { if (!o) setArciRevert(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Segnare la tessera ARCI come NON pagata?</AlertDialogTitle>
            <AlertDialogDescription>La tessera tornerà allo stato "non pagata" e il metodo di pagamento verrà azzerato.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="arci-revert-confirm" onClick={confirmArciRevert} className="bg-amber-600 text-white hover:bg-amber-700">Conferma</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
